import { useMemo, useCallback } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import ExpertsPerformance from "./ExpertsPerformance";
import { Card, CardBody, CardHeader, Container, Row } from "reactstrap";
import { Link } from "react-router-dom";
import TableContainer from "../../Components/Common/TableContainer";
import Loader from "../../Components/Common/Loader";
import { isPendingExpertRequest, useExperts } from "../../hooks/useExperts";
import { getStoredAuthUser } from "../../utils/backendApi";

import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";

/** "DD/MM/YYYY HH:mm", or null when the API sent nothing usable. */
const formatRequestDate = (iso) => {
  if (!iso) return null;
  const parsed = moment(iso);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : null;
};

const competenceLabels = (expert) =>
  (Array.isArray(expert?.competences) ? expert.competences : [])
    .map((c) => c?.label)
    .filter(Boolean);

const Experts = (props) => {
  const role = useMemo(() => {
    const stored = getStoredAuthUser()?.role;
    return Array.isArray(stored) ? stored : [];
  }, []);

  const expertsQ = useExperts();

  // Memoised so the derived lists below only recompute when the data changes.
  const dataexperts = useMemo(() => {
    const raw = expertsQ.data;
    return Array.isArray(raw) ? raw : raw?.results ?? [];
  }, [expertsQ.data]);

  const errorMessage =
    typeof expertsQ.error === "string"
      ? expertsQ.error
      : expertsQ.error?.message || "Erreur";

  // Applications from the mobile app, newest first, derived from the list
  // already loaded so the section and the table can never disagree. Declared
  // before the early returns below: hooks must run in the same order on every
  // render.
  const pendingRequests = useMemo(
    () =>
      dataexperts
        .filter(isPendingExpertRequest)
        .sort((a, b) =>
          String(b?.expert_requested_at ?? "").localeCompare(
            String(a?.expert_requested_at ?? "")
          )
        ),
    [dataexperts]
  );
  const orderedExperts = useMemo(
    () => [
      ...pendingRequests,
      ...dataexperts.filter((expert) => !isPendingExpertRequest(expert)),
    ],
    [dataexperts, pendingRequests]
  );

  document.title = props.t("Liste des experts");
  const checkedAll = useCallback(() => {
    const checkall = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".entrepriseCheckBox");

    if (checkall.checked) {
      ele.forEach((ele) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele) => {
        ele.checked = false;
      });
    }
  }, []);
  const columns = useMemo(
    () => [
      {
        Header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            onClick={() => checkedAll()}
          />
        ),
        Cell: (cellProps) => {
          return (
            <input
              type="checkbox"
              className="entrepriseCheckBox"
              value={cellProps.row.original.id}
            />
          );
        },
        id: "#",
      },
      {
        Header: props.t("NOM "),
        Cell: (expert) => (
          <>
            <div className="d-flex align-items-center ">
              <div className="flex-grow-1">
                <h5
                  className="fs-14 mb-1 italic"
                  style={{ textTransform: "uppercase", fontWeight: "Bold" }}
                >
                  <Link
                    to={`/experts-details/${expert.row.original.id}`}
                    className="text-dark"
                  >
                    {" "}
                    {expert.row.original.user?.username || "-"}
                  </Link>
                </h5>
              </div>
            </div>
          </>
        ),
      },
      {
        Header: props.t(" "),
        Cell: (cellProps) => {
          return (
            <h5 className="fs-14 mb-1">
              {/* Single source of truth: the badge follows is_certified,
                  set by the certify action. It used to require three separate
                  flags, so a certified professional showed nothing. */}
              {cellProps.row.original.is_certified && (
                <div className="flex aligns-items-center justify-items">
                  <span className="badge badge-soft-success me-1">
                    <i className="ri-verified-badge-fill me-1" />
                    {props.t("Certifié")}
                  </span>
                </div>
              )}

              {/* An application from the mobile app nobody has decided on.
                  `expert_request` was returned all along; nothing read it. */}
              {isPendingExpertRequest(cellProps.row.original) && (
                <span className="badge badge-soft-warning me-1">
                  {props.t("Candidature en attente")}
                </span>
              )}

              {cellProps.row.original.is_validated === false &&
                cellProps.row.original.validation_report && (
                  <span className="badge badge-soft-danger me-1">
                    {props.t("Non validé")}
                  </span>
                )}

              {cellProps.row.original.is_approved &&
                cellProps.row.original.message &&
                !cellProps.row.original.is_certified && (
                  <span className="badge badge-soft-danger me-1">
                    {props.t("Non approuvé")}
                  </span>
                )}
            </h5>
          );
        },
      },
      {
        Header: props.t("EMAIL"),
        Cell: (expert) => (
          <>
            <div className="d-flex align-items-center ">
              <div className="flex-grow-1">
                <h5 className="fs-14 mb-1 italic">
                  <Link
                    to={`/experts-details/${expert.row.original.id}`}
                    className="text-dark"
                  >
                    {" "}
                    {expert.row.original.user?.email || "-"}
                  </Link>
                </h5>
              </div>
            </div>
          </>
        ),
      },
      {
        Header: props.t("TELEPHONE"),
        Cell: (expert) => (
          <>
            <div className="d-flex align-items-center ">
              <div className="flex-grow-1">
                <h5 className="fs-14 mb-1 italic">
                  <Link
                    to={`/experts-details/${expert.row.original.id}`}
                    className="text-dark"
                  >
                    {" "}
                    {expert.row.original.user?.phone_number || "-"}
                  </Link>
                </h5>
              </div>
            </div>
          </>
        ),
      },
      {
        Header: props.t("FORMATION"),
        accessor: "formation",
        Cell: (expert) => (
          <>
            <div className="d-flex align-items-center ">
              <div className="flex-grow-1">
                <h5
                  className="fs-14 mb-1 italic"
                  style={{ textTransform: "uppercase" }}
                >
                  <Link
                    to={`/experts-details/${expert.row.original.id}`}
                    className="text-dark"
                  >
                    {" "}
                    {expert.row.original.formation || "-"}
                  </Link>
                </h5>
              </div>
            </div>
          </>
        ),
      },
      {
        Header: props.t("PROFESSION"),
        accessor: "profession",
        Cell: (expert) => (
          <>
            <div className="d-flex align-items-center ">
              <div className="flex-grow-1">
                <h5 className="fs-14 mb-1 italic">
                  <Link
                    to={`/experts-details/${expert.row.original.id}`}
                    className="text-dark"
                  >
                    {" "}
                    {expert.row.original.profession || "-"}
                  </Link>
                </h5>
              </div>
            </div>
          </>
        ),
      },
      {
        Header: props.t("DIPLOME"),
        accessor: "last_certificate",
        Cell: (expert) => (
          <>
            <div className="d-flex align-items-center ">
              <div className="flex-grow-1">
                <h5 className="fs-14 mb-1 italic">
                  <Link
                    to={`/experts-details/${expert.row.original.id}`}
                    className="text-dark"
                  >
                    {" "}
                    {expert.row.original.last_certificate || "-"}
                  </Link>
                </h5>
              </div>
            </div>
          </>
        ),
      },
      {
        // What the platform wanted recorded per expert: how many requesters
        // they saw through, and how satisfied those requesters were.
        Header: props.t("DEMANDEURS"),
        Cell: (expert) => (
          <h5 className="fs-14 mb-1">
            <span className="badge badge-soft-info">
              {expert.row.original.questions_handled || 0}
            </span>
          </h5>
        ),
      },
      {
        Header: props.t("SATISFACTION"),
        Cell: (expert) => {
          const rate = expert.row.original.satisfaction_rate;
          // null is "nobody has judged them yet", which must not read as 0 %.
          if (rate === null || rate === undefined) {
            return (
              <span className="text-muted fs-12">
                {props.t("Aucune évaluation")}
              </span>
            );
          }
          const tone = rate >= 80 ? "success" : rate >= 50 ? "warning" : "danger";
          return (
            <h5 className="fs-14 mb-1">
              <span className={`badge badge-soft-${tone}`}>{rate} %</span>
            </h5>
          );
        },
      },
      {
        Header: props.t("VALIDATION"),
        Cell: (cellProps) => {
          return (
            <div
              className="d-flex align-items-center space-x-2"
              style={{ width: "150px" }}
            >
              <div className="fs-14 ">
                {/* Anyone not yet validated can be validated (an earlier
                    written report no longer locks the button), and anyone
                    validated but neither approved nor already an expert can be
                    approved (an earlier rejection message no longer locks it). */}
                {!cellProps.row.original.is_validated &&
                (role.some((role) => role === "commite") ||
                  role.some((role) => role === "admin")) ? (
                  <div className=" aligns-items-center justify-items">
                    <div
                      className="btn btn-soft-danger"
                      style={{ fontSize: "13px", margin: "0px" }}
                    >
                      <Link
                        to={`/experts-details/${cellProps.row.original.id}`}
                      >
                        Valider
                      </Link>
                    </div>
                  </div>
                ) : cellProps.row.original.is_validated &&
                  !cellProps.row.original.is_approved &&
                  !cellProps.row.original.is_expert &&
                  role.some((role) => role === "admin") ? (
                  <div className=" aligns-items-center justify-items">
                    <div
                      className="btn btn-soft-success"
                      style={{ fontSize: "13px" }}
                    >
                      <Link
                        to={`/experts-details/${cellProps.row.original.id}`}
                      >
                        Approuver
                      </Link>
                    </div>
                  </div>
                ) : null}
              </div>
              {!cellProps.row.original.is_certified && (
                <div
                  className="btn btn-soft-warning"
                  style={{ fontSize: "13px" }}
                >
                  <Link to={`/experts-details/${cellProps.row.original.id}`}>
                    Certifier
                  </Link>
                </div>
              )}
            </div>
          );
        },
      },
      {
        Header: props.t("ACTIONS"),
        Cell: (cellProps) => {
          return (
            <ul className="list-inline hstack gap-2 mb-0">
              <li className="list-inline-item" title={props.t("Voir")}>
                <Link
                  to={`/experts-details/${cellProps.row.original.id}`}
                  className="text-warning d-inline-block"
                >
                  <i className="ri-eye-fill align-bottom"></i>
                </Link>
              </li>
            </ul>
          );
        },
      },
    ],
    [props, role, checkedAll]
  );

  if (expertsQ.isLoading) return <Loader />;
  if (expertsQ.isError) return <Loader error={errorMessage} />;

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} />
      <Container fluid>
        <BreadCrumb
          title={props.t("Experts")}
          pageTitle={props.t("Liste des experts")}
        />

        {/* How the experts are performing, derived from the list already
            loaded: no extra request, and the totals cannot disagree with the
            table below. */}
        {!expertsQ.isLoading && !expertsQ.isError ? (
          <ExpertsPerformance experts={dataexperts} t={props.t} />
        ) : null}

        {/* Applications from the mobile app. Until now they landed in the
            table below with nothing to tell them apart, so the committee never
            saw them. The section renders even when empty so the absence of a
            request is a stated fact rather than a missing block. */}
        <Card>
          <CardHeader className="d-flex align-items-center">
            <h5 className="card-title mb-0 flex-grow-1">
              {props.t("Demandes en attente")}
            </h5>
            <span
              className={`badge rounded-pill ${
                pendingRequests.length > 0 ? "bg-warning" : "bg-light text-muted"
              }`}
            >
              {pendingRequests.length}
            </span>
          </CardHeader>
          <CardBody>
            {pendingRequests.length === 0 ? (
              <p className="text-muted mb-0">
                {props.t("Aucune demande en attente")}
              </p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead className="table-light text-muted">
                    <tr>
                      <th>{props.t("Nom")}</th>
                      <th>{props.t("Email")}</th>
                      <th>{props.t("Compétences déclarées")}</th>
                      <th>{props.t("Date")}</th>
                      <th>{props.t("Statut")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRequests.map((expert) => {
                      const labels = competenceLabels(expert);
                      const requestedAt = formatRequestDate(
                        expert.expert_requested_at
                      );
                      return (
                        <tr key={expert.id}>
                          <td className="fw-semibold text-uppercase">
                            <Link
                              to={`/experts-details/${expert.id}`}
                              className="text-dark"
                            >
                              {expert.user?.username || "-"}
                            </Link>
                          </td>
                          <td>{expert.user?.email || "-"}</td>
                          <td>
                            {labels.length > 0 ? (
                              labels.join(", ")
                            ) : (
                              <span className="text-muted">
                                {props.t("(Aucune compétence déclarée)")}
                              </span>
                            )}
                          </td>
                          <td>
                            {requestedAt || (
                              <span className="text-muted">
                                {props.t("Date de demande inconnue")}
                              </span>
                            )}
                          </td>
                          <td>
                            {/* The committee validates first, then an admin
                                approves: the label says whose turn it is. */}
                            <span
                              className={`badge badge-soft-${
                                expert.is_validated ? "info" : "warning"
                              }`}
                            >
                              {expert.is_validated
                                ? props.t("À approuver (admin)")
                                : props.t("À valider (comité)")}
                            </span>
                          </td>
                          <td className="text-end">
                            <Link
                              to={`/experts-details/${expert.id}`}
                              className="btn btn-sm btn-soft-warning"
                            >
                              {props.t("Examiner la demande")}
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>

        <Row>
          <div className="col-xl-12 col-lg-12">
            <div>
              <div className="card">
                <div className="card-body pt-0">
                  {dataexperts && dataexperts.length > 0 ? (
                    <div>
                      <TableContainer
                        columns={columns}
                        data={orderedExperts}
                        isGlobalFilter={true}
                        isAddUserList={false}
                        customPageSize={10}
                        divClass="table-responsive mb-1"
                        tableClass="mb-0 align-middle"
                        theadClass="table-light text-muted"
                        // isExpertFilter={true}
                      />
                    </div>
                  ) : (
                    <div className="py-4 text-center">
                      <div className="mt-4">
                        <h5>{props.t("Aucun expert disponible pour le moment")}</h5>
                        <p className="text-muted mb-0">
                          {props.t(
                            "Les profils experts en attente et certifiés apparaissent ici dès qu'ils sont créés."
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};

Experts.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};
export default withRouter(withTranslation()(Experts));
